import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Outlet, useLocation } from 'react-router-dom';

import { BrandingContext } from '../context/BrandingContext';
import {
  applyAccentColor,
  applyFavicon,
  applyPrimaryColor,
  resetAccentColor,
  resetFavicon,
  resetPrimaryColor,
} from '../context/brandingTheme';
import { TenantProvider } from '../context/TenantContext';
import { ConfigQueryKeys, getBranding } from '../features/config/api';
import { Footer } from './Footer';
import { Header } from './Header';
import { Logo } from './Logo';
import { MobileDrawer } from './MobileDrawer';
import { Sidebar, SidebarNav } from './Sidebar';

/**
 * Authenticated app shell — sidebar on the left (drawer on mobile), header on
 * top, scrollable outlet for the active route below. Mounted under
 * `<RouteGuard requireAuth>`.
 */
export function Layout(): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // White-label branding (M1): load once after auth and apply the tenant colour at runtime.
  const branding = useQuery({
    queryKey: ConfigQueryKeys.branding,
    queryFn: ({ signal }) => getBranding(signal),
    staleTime: 5 * 60_000,
  });

  // Navigating from the drawer should dismiss it.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (branding.data === undefined) {
      return;
    }
    applyPrimaryColor(branding.data.primaryColorHex);
    applyAccentColor(branding.data.secondaryColorHex, branding.data.primaryColorHex);
    applyFavicon(branding.data.faviconUrl);
    return () => {
      resetPrimaryColor();
      resetAccentColor();
      resetFavicon();
    };
  }, [branding.data]);

  const brandingValue = {
    productName: branding.data?.productName ?? 'DocuSense',
    logoUrl: branding.data?.logoUrl ?? null,
    logoHeightPx: branding.data?.logoHeightPx ?? 32,
  };

  return (
    <BrandingContext.Provider value={brandingValue}>
    <TenantProvider>
      <div className="flex h-full min-h-screen w-full bg-paper">
        <Sidebar />
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} label="Menú de navegación">
          <div className="flex h-14 items-center border-b border-slate-200 px-4">
            <Logo size={26} />
          </div>
          <SidebarNav />
        </MobileDrawer>
        <div className="flex min-w-0 flex-1 flex-col">
          <Header onMenuClick={() => setDrawerOpen(true)} />
          <main className="flex-1 overflow-y-auto p-4 sm:p-6">
            <Outlet />
          </main>
          <Footer />
        </div>
      </div>
    </TenantProvider>
    </BrandingContext.Provider>
  );
}
